import { useEffect, useState } from 'react';
import { App } from '@capacitor/app';
import { isNativePlatform } from '@/lib/platform';
import { debug } from '@/utils/logger';
import { getRuntimeEnvironment } from '@/config/runtimeEnvironment';
import { formatVersionWithEnvironment } from '@/config/versionDisplay';

/**
 * Resolves the display string for the running build — "v2.8.13 (build 473) · prod".
 *
 * Native reads the real installed bundle through Capacitor; web has no such
 * source, so it falls back to the values baked in at build time. Returns '' until
 * the native lookup resolves, and whenever the version/build are unavailable, so
 * callers can render nothing rather than a half-empty line.
 */
export function useAppVersionInfo(): string {
  const [versionInfo, setVersionInfo] = useState('');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const runtimeEnvironment = getRuntimeEnvironment();

      if (isNativePlatform()) {
        try {
          const info = await App.getInfo();
          if (cancelled) return;
          setVersionInfo(formatVersionWithEnvironment(info.version, info.build, runtimeEnvironment));
        } catch (err) {
          debug('[useAppVersionInfo] Could not get app info', err);
        }
        return;
      }

      setVersionInfo(
        formatVersionWithEnvironment(
          process.env.NEXT_PUBLIC_APP_VERSION,
          process.env.NEXT_PUBLIC_APP_BUILD,
          runtimeEnvironment,
        ),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return versionInfo;
}

export default useAppVersionInfo;
